import { useMemo, useState } from 'react';
import {
  ArrowUpRight,
  BarChart3,
  LineChart,
  Calendar,
  Users,
  Megaphone,
  X,
  Gauge,
  Zap,
  Trophy,
} from 'lucide-react';
import { Button, FilterCard, KPI_TOOLTIPS } from '@/components/ui';
import type { MondayUser } from '@/services/api/monday';
import {
  formatCurrency,
  calculateUnifiedHistoricalAnalytics,
  calculateWeightedPipeline,
  calculateLeadVelocity,
  calculateWinLossAnalysis,
  calculateRepPerformance,
  identifySlippedDeals,
  calculateLeadToDealConversion,
} from '@/utils';
import type { StageTransition } from '@/services/api/monday';
import {
  StageVelocityCard,
  ConversionFunnelChart,
  LeadAgingTable,
  ActivityTrendsChart,
  LeadToDealTracker,
  AnalyticsDetailModal,
  AIAnalysis,
  RepLeaderboard,
  LeadSourcesCard,
  SlippedDealsPanel,
} from './analytics';
import { StatCard } from './StatCard';
import type { AnalyticsDetailData } from './analytics';
import type { Lead, Deal, Visitor, Campaign, LeadList } from '@/types';

interface DashboardProps {
  leads: Lead[];
  deals: Deal[];
  visitors: Visitor[];
  campaigns: Campaign[];
  leadLists: LeadList[];
  mondayUsers?: MondayUser[];
  stageTransitions?: StageTransition[];
  leadsLoading?: boolean;
  dealsLoading?: boolean;
  visitorsLoading?: boolean;
  stageTransitionsLoading?: boolean;
  onNavigate: (tab: 'visitors' | 'leads' | 'campaigns' | 'lists') => void;
}

export function Dashboard({
  leads,
  deals,
  visitors: _visitors,
  campaigns: _campaigns,
  leadLists: _leadLists,
  mondayUsers = [],
  stageTransitions = [],
  leadsLoading = false,
  dealsLoading = false,
  stageTransitionsLoading = false,
  onNavigate,
}: DashboardProps) {
  // Calculate default date range (last 3 months) - memoized to prevent recalculation
  const defaultDateRange = useMemo(() => {
    const end = new Date();
    const start = new Date();
    start.setMonth(start.getMonth() - 3);
    return {
      start: start.toISOString().split('T')[0] ?? '',
      end: end.toISOString().split('T')[0] ?? '',
    };
  }, []);

  // Date range filter state for analytics - default to last 3 months
  const [analyticsStartDate, setAnalyticsStartDate] = useState<string>(defaultDateRange.start);
  const [analyticsEndDate, setAnalyticsEndDate] = useState<string>(defaultDateRange.end);

  // Date range filter state for leaderboard - default to last 3 months (independent from analytics)
  const [leaderboardStartDate, setLeaderboardStartDate] = useState<string>(defaultDateRange.start);
  const [leaderboardEndDate, setLeaderboardEndDate] = useState<string>(defaultDateRange.end);

  // Rep filter state
  const [selectedRep, setSelectedRep] = useState<string>('all');

  // Southwest Airlines exclusion toggle (excluded by default)
  const [includeSouthwestAirlines, setIncludeSouthwestAirlines] = useState<boolean>(false);

  // Active Leads Only toggle — hides Closed Won, Closed Lost, Unqualified stages
  const [activeLeadsOnly, setActiveLeadsOnly] = useState<boolean>(
    () => localStorage.getItem('dashboardActiveLeadsOnly') !== 'false'
  );
  const setActiveLeadsOnlyPersist = (v: boolean) => {
    setActiveLeadsOnly(v);
    localStorage.setItem('dashboardActiveLeadsOnly', String(v));
  };
  const isInactiveStage = (stage: string): boolean => {
    const s = (stage || '').toLowerCase();
    return s.includes('closed won') || s.includes('closed lost') ||
           s.includes('unqualified') || s.includes('disqualified') ||
           s.includes('lost');
  };

  // Helper to check if a string matches "Southwest Airlines" pattern
  const isSouthwestAirlines = (text: string): boolean => {
    const normalized = (text || '').toLowerCase();
    return normalized.includes('southwest') && (normalized.includes('airline') || normalized.includes('air'));
  };

  // Filter out Southwest Airlines from base data when toggle is off
  const baseLeads = useMemo(() => {
    let result = leads;
    if (!includeSouthwestAirlines) result = result.filter(lead => !isSouthwestAirlines(lead.company));
    if (activeLeadsOnly) result = result.filter(lead => !isInactiveStage(lead.stage));
    return result;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leads, includeSouthwestAirlines, activeLeadsOnly]);

  const baseDeals = useMemo(() => {
    if (includeSouthwestAirlines) return deals;
    return deals.filter(deal => !isSouthwestAirlines(deal.company) && !isSouthwestAirlines(deal.name));
  }, [deals, includeSouthwestAirlines]);

  const baseStageTransitions = useMemo(() => {
    if (includeSouthwestAirlines) return stageTransitions;
    const excludedLeadIds = new Set(
      leads
        .filter(lead => isSouthwestAirlines(lead.company))
        .map(lead => lead.id)
    );
    return stageTransitions.filter(t => !excludedLeadIds.has(t.itemId));
  }, [stageTransitions, leads, includeSouthwestAirlines]);

  // Analytics detail modal state
  const [analyticsDetail, setAnalyticsDetail] = useState<AnalyticsDetailData | null>(null);

  // Create a normalized lookup for Monday users and aggregate all owner names
  const { userPhotoMap, repCounts, canonicalNameMap } = useMemo(() => {
    const photoMap: Record<string, string | undefined> = {};
    const mondayNameMap: Record<string, string> = {}; // normalized -> canonical name

    // Build lookup from Monday users
    mondayUsers.forEach((user) => {
      const canonical = user.name;
      photoMap[canonical] = user.photo_small || user.photo_thumb || user.photo_original;

      // Map various normalized forms to canonical name
      mondayNameMap[canonical.toLowerCase().trim()] = canonical;
      // Also map first name only
      const firstName = canonical.split(' ')[0];
      if (firstName && !mondayNameMap[firstName.toLowerCase()]) {
        mondayNameMap[firstName.toLowerCase()] = canonical;
      }
    });

    // Helper to get canonical rep name
    const getCanonical = (owner: string | undefined): string => {
      if (!owner) return 'Unassigned';
      const trimmed = owner.trim();
      if (!trimmed) return 'Unassigned';
      const normalized = trimmed.toLowerCase();

      // Check exact match first
      if (mondayNameMap[normalized]) {
        return mondayNameMap[normalized];
      }

      // Check if it's a first name match
      const firstName = normalized.split(' ')[0];
      if (firstName && mondayNameMap[firstName]) {
        return mondayNameMap[firstName];
      }

      // No Monday user match - normalize by title casing
      // This merges "deleted", "Deleted", "DELETED" etc.
      return trimmed.split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
    };

    // Build canonical name map from all leads (using baseLeads to respect exclusion toggle)
    const nameMap: Record<string, string> = {}; // original -> canonical
    const counts: Record<string, number> = { all: baseLeads.length };

    baseLeads.forEach(lead => {
      const original = lead.owner || '';
      if (!nameMap[original]) {
        nameMap[original] = getCanonical(lead.owner);
      }
      const canonical = nameMap[original];
      counts[canonical] = (counts[canonical] || 0) + 1;
    });

    // Also update photo map for canonical names
    Object.values(nameMap).forEach(canonical => {
      if (!photoMap[canonical]) {
        // Try to find a matching Monday user by normalized name
        const normalized = canonical.toLowerCase();
        const matchingUser = mondayUsers.find(u =>
          u.name.toLowerCase() === normalized ||
          u.name.split(' ')[0]?.toLowerCase() === normalized.split(' ')[0]
        );
        if (matchingUser) {
          photoMap[canonical] = matchingUser.photo_small || matchingUser.photo_thumb || matchingUser.photo_original;
        }
      }
    });

    return { userPhotoMap: photoMap, repCounts: counts, canonicalNameMap: nameMap };
  }, [mondayUsers, baseLeads]);

  // Helper to get canonical rep name (using precomputed map)
  const getCanonicalRepName = (owner: string | undefined): string => {
    if (!owner) return 'Unassigned';
    if (canonicalNameMap[owner]) return canonicalNameMap[owner];
    // Fallback for any edge cases
    const trimmed = owner.trim();
    if (!trimmed) return 'Unassigned';
    return trimmed.split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Calculate analytics
  const analyticsLoading = leadsLoading || dealsLoading;

  // First, filter leads by rep (before analytics calculations)
  // Uses baseLeads/baseDeals which already have Southwest Airlines exclusion applied
  // Handles comma-separated owners (e.g., "Jake Evans, Gabrielle Fisher")
  const repFilteredLeads = useMemo(() => {
    if (selectedRep === 'all') return baseLeads;
    return baseLeads.filter(l => {
      const owner = l.owner || '';
      if (owner.includes(',')) {
        // Check if any of the comma-separated owners match
        return owner.split(',').map(o => getCanonicalRepName(o.trim())).includes(selectedRep);
      }
      return getCanonicalRepName(owner) === selectedRep;
    });
  }, [baseLeads, selectedRep, canonicalNameMap]);

  const repFilteredDeals = useMemo(() => {
    if (selectedRep === 'all') return baseDeals;
    return baseDeals.filter(d => {
      const owner = d.owner || '';
      if (owner.includes(',')) {
        // Check if any of the comma-separated owners match
        return owner.split(',').map(o => getCanonicalRepName(o.trim())).includes(selectedRep);
      }
      return getCanonicalRepName(owner) === selectedRep;
    });
  }, [baseDeals, selectedRep, canonicalNameMap]);

  // Filter stage transitions to only include those for rep-filtered leads
  const repFilteredTransitions = useMemo(() => {
    if (selectedRep === 'all') return baseStageTransitions;
    const leadIds = new Set(repFilteredLeads.map(l => l.id));
    return baseStageTransitions.filter(t => leadIds.has(t.itemId));
  }, [baseStageTransitions, repFilteredLeads, selectedRep]);

  // Calculate all analytics in one pass using unified function
  // Now uses rep-filtered data
  const unifiedAnalytics = useMemo(
    () => calculateUnifiedHistoricalAnalytics(
      repFilteredLeads,
      repFilteredDeals,
      repFilteredTransitions,
      analyticsStartDate || undefined,
      analyticsEndDate || undefined
    ),
    [repFilteredLeads, repFilteredDeals, repFilteredTransitions, analyticsStartDate, analyticsEndDate]
  );

  // Destructure for convenience
  const {
    funnel: historicalFunnel,
    stageVelocity,
    leadAging,
    activityTrends,
  } = unifiedAnalytics;

  // For components that need the filtered leads array (with date filter too)
  const filteredLeads = useMemo(() => {
    const result = repFilteredLeads;

    // Apply date filter
    if (!analyticsStartDate && !analyticsEndDate) return result;

    // Get lead IDs with transitions in date range
    const leadsWithTransitions = new Set(
      repFilteredTransitions
        .filter((t) => {
          const date = t.timestamp?.split('T')[0] ?? '';
          if (!date) return true;
          if (analyticsStartDate && date < analyticsStartDate) return false;
          if (analyticsEndDate && date > analyticsEndDate) return false;
          return true;
        })
        .map((t) => t.itemId)
    );

    return result.filter((lead) => {
      // Include if lead had transitions in date range
      if (leadsWithTransitions.has(lead.id)) return true;

      // Also include if created in date range
      const createdDate = lead.createdAt?.split('T')[0] ?? '';
      if (!createdDate) return true;
      if (analyticsStartDate && createdDate < analyticsStartDate) return false;
      if (analyticsEndDate && createdDate > analyticsEndDate) return false;
      return true;
    });
  }, [repFilteredLeads, repFilteredTransitions, analyticsStartDate, analyticsEndDate]);

  const filteredDeals = useMemo(() => {
    const result = repFilteredDeals;

    // Apply date filter
    if (!analyticsStartDate && !analyticsEndDate) return result;

    return result.filter((deal) => {
      const createdDate = deal.createdAt?.split('T')[0] ?? '';
      if (!createdDate) return true;
      if (analyticsStartDate && createdDate < analyticsStartDate) return false;
      if (analyticsEndDate && createdDate > analyticsEndDate) return false;
      return true;
    });
  }, [repFilteredDeals, analyticsStartDate, analyticsEndDate]);

  // Date-filtered ALL deals (not rep-filtered) for conversion tracking
  // This is needed because deals often have different owners than leads
  const dateFilteredAllDeals = useMemo(() => {
    if (!analyticsStartDate && !analyticsEndDate) return baseDeals;

    return baseDeals.filter((deal) => {
      const createdDate = deal.createdAt?.split('T')[0] ?? '';
      if (!createdDate) return true;
      if (analyticsStartDate && createdDate < analyticsStartDate) return false;
      if (analyticsEndDate && createdDate > analyticsEndDate) return false;
      return true;
    });
  }, [baseDeals, analyticsStartDate, analyticsEndDate]);

  // Global lead-to-deal conversion using ALL leads (not rep-filtered)
  // This ensures consistent matching regardless of rep filter
  const globalLeadToDealConversion = useMemo(
    () => calculateLeadToDealConversion(baseLeads, dateFilteredAllDeals),
    [baseLeads, dateFilteredAllDeals]
  );

  // Date-filtered lead-to-deal conversion for analytics display
  // When a rep is selected, filter the global conversions by owner
  // This matches how the leaderboard calculates rep conversions
  const dateFilteredLeadToDealConversion = useMemo(() => {
    if (selectedRep === 'all') {
      // Apply date filter to leads for total count
      const dateFilteredLeadIds = new Set(filteredLeads.map(l => l.id));
      const filteredConversions = globalLeadToDealConversion.convertedLeads.filter(
        c => dateFilteredLeadIds.has(c.lead.id)
      );
      const conversionRate = filteredLeads.length > 0
        ? Math.round((filteredConversions.length / filteredLeads.length) * 100)
        : 0;
      const avgDaysToConvert = filteredConversions.length > 0
        ? Math.round(filteredConversions.reduce((sum, c) => sum + c.daysToConvert, 0) / filteredConversions.length)
        : 0;

      return {
        ...globalLeadToDealConversion,
        convertedLeads: filteredConversions,
        totalLeads: filteredLeads.length,
        convertedCount: filteredConversions.length,
        conversionRate,
        avgDaysToConvert,
      };
    }

    // Filter conversions to those where the lead is owned by the selected rep
    // Handle comma-separated owners
    const repFilteredConversions = globalLeadToDealConversion.convertedLeads.filter(c => {
      const owner = c.lead.owner || '';
      if (owner.includes(',')) {
        return owner.split(',').map(o => getCanonicalRepName(o.trim())).includes(selectedRep);
      }
      return getCanonicalRepName(owner) === selectedRep;
    });

    // Calculate metrics based on filtered data
    const conversionRate = filteredLeads.length > 0
      ? Math.round((repFilteredConversions.length / filteredLeads.length) * 100)
      : 0;
    const avgDaysToConvert = repFilteredConversions.length > 0
      ? Math.round(repFilteredConversions.reduce((sum, c) => sum + c.daysToConvert, 0) / repFilteredConversions.length)
      : 0;

    // Rebuild conversionsByLeadStage for filtered data
    const conversionsByLeadStage: Record<string, number> = {};
    for (const c of repFilteredConversions) {
      const stage = c.lead.stage || 'Unknown';
      conversionsByLeadStage[stage] = (conversionsByLeadStage[stage] || 0) + 1;
    }

    return {
      convertedLeads: repFilteredConversions,
      totalLeads: filteredLeads.length,
      totalDeals: globalLeadToDealConversion.totalDeals,
      convertedCount: repFilteredConversions.length,
      conversionRate,
      avgDaysToConvert,
      conversionsByLeadStage,
      conversionsByDealStage: globalLeadToDealConversion.conversionsByDealStage,
    };
  }, [globalLeadToDealConversion, filteredLeads, selectedRep, getCanonicalRepName]);

  const hasCustomDateFilter = analyticsStartDate !== defaultDateRange.start || analyticsEndDate !== defaultDateRange.end;
  const hasActiveFilters = selectedRep !== 'all' || hasCustomDateFilter;

  const resetDateFilter = () => {
    setAnalyticsStartDate(defaultDateRange.start);
    setAnalyticsEndDate(defaultDateRange.end);
  };

  const resetAllFilters = () => {
    setSelectedRep('all');
    resetDateFilter();
  };

  // Top KPI metrics - use base data (only Southwest Airlines toggle applied, no date/rep filters)
  // Filter to only include deals still in the pipeline (exclude closed deals)
  const pipelineDeals = useMemo(() => {
    return baseDeals.filter(deal => {
      const stage = (deal.stage || '').toLowerCase();
      return !stage.includes('closed') && !stage.includes('won') && !stage.includes('lost');
    });
  }, [baseDeals]);

  const weightedPipeline = useMemo(
    () => calculateWeightedPipeline(pipelineDeals),
    [pipelineDeals]
  );

  // Rolling 1-month date range for Win Rate KPI
  const rolling1MonthStart = useMemo(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 1);
    return date.toISOString().split('T')[0] ?? '';
  }, []);

  // Filter closed deals for rolling 1 month (for Win Rate KPI)
  // Only includes won/lost deals, filtered by their close date
  const rolling1MonthClosedDeals = useMemo(() => {
    // First filter to only closed deals
    const closedDeals = baseDeals.filter(deal => {
      const stage = (deal.stage || '').toLowerCase();
      return stage.includes('won') || stage.includes('lost');
    });
    // Then filter by close date (exclude deals without close date)
    return closedDeals.filter(deal => {
      const closeDate = deal.closeDate?.split('T')[0] ?? '';
      if (!closeDate) return false;
      return closeDate >= rolling1MonthStart;
    });
  }, [baseDeals, rolling1MonthStart]);

  // Lead Velocity Rate (LVR) - month-over-month growth of qualified leads
  const leadVelocity = useMemo(
    () => calculateLeadVelocity(baseLeads),
    [baseLeads]
  );

  // Win/Loss for top KPI card (rolling 1 month)
  const overallWinLossAnalysis = useMemo(
    () => calculateWinLossAnalysis(rolling1MonthClosedDeals),
    [rolling1MonthClosedDeals]
  );

  // Deals for general analytics (filtered by createdAt)
  const dealsFromRepLeads = useMemo(() => {
    const deals = selectedRep === 'all'
      ? repFilteredDeals
      : dateFilteredLeadToDealConversion.convertedLeads.map(c => c.deal);

    if (!analyticsStartDate && !analyticsEndDate) return deals;

    return deals.filter((deal) => {
      const createdDate = deal.createdAt?.split('T')[0] ?? '';
      if (!createdDate) return true;
      if (analyticsStartDate && createdDate < analyticsStartDate) return false;
      if (analyticsEndDate && createdDate > analyticsEndDate) return false;
      return true;
    });
  }, [selectedRep, repFilteredDeals, dateFilteredLeadToDealConversion.convertedLeads, analyticsStartDate, analyticsEndDate]);

  const slippedDeals = useMemo(
    () => identifySlippedDeals(dealsFromRepLeads),
    [dealsFromRepLeads]
  );

  // Rep Leaderboard should always show ALL reps regardless of rep filter
  // Has its own date filter independent from analytics (respects Southwest Airlines exclusion toggle)
  const dateFilteredLeadsForLeaderboard = useMemo(() => {
    if (!leaderboardStartDate && !leaderboardEndDate) return baseLeads;

    // Get lead IDs with transitions in date range
    const leadsWithTransitions = new Set(
      baseStageTransitions
        .filter((t) => {
          const date = t.timestamp?.split('T')[0] ?? '';
          if (!date) return true;
          if (leaderboardStartDate && date < leaderboardStartDate) return false;
          if (leaderboardEndDate && date > leaderboardEndDate) return false;
          return true;
        })
        .map((t) => t.itemId)
    );

    return baseLeads.filter((lead) => {
      if (leadsWithTransitions.has(lead.id)) return true;
      const createdDate = lead.createdAt?.split('T')[0] ?? '';
      if (!createdDate) return true;
      if (leaderboardStartDate && createdDate < leaderboardStartDate) return false;
      if (leaderboardEndDate && createdDate > leaderboardEndDate) return false;
      return true;
    });
  }, [baseLeads, baseStageTransitions, leaderboardStartDate, leaderboardEndDate]);

  const dateFilteredDealsForLeaderboard = useMemo(() => {
    if (!leaderboardStartDate && !leaderboardEndDate) return baseDeals;

    return baseDeals.filter((deal) => {
      const createdDate = deal.createdAt?.split('T')[0] ?? '';
      if (!createdDate) return true;
      if (leaderboardStartDate && createdDate < leaderboardStartDate) return false;
      if (leaderboardEndDate && createdDate > leaderboardEndDate) return false;
      return true;
    });
  }, [baseDeals, leaderboardStartDate, leaderboardEndDate]);

  // Calculate lead-to-deal conversion for leaderboard using ALL leads and date-filtered deals
  const leaderboardLeadToDealConversion = useMemo(
    () => calculateLeadToDealConversion(baseLeads, dateFilteredDealsForLeaderboard),
    [baseLeads, dateFilteredDealsForLeaderboard]
  );

  // Rep performance for leaderboard - shows ALL reps regardless of rep filter
  const leaderboardRepPerformance = useMemo(
    () => calculateRepPerformance(dateFilteredLeadsForLeaderboard, dateFilteredDealsForLeaderboard, leaderboardLeadToDealConversion),
    [dateFilteredLeadsForLeaderboard, dateFilteredDealsForLeaderboard, leaderboardLeadToDealConversion]
  );

  // Calculate source distribution from filtered leads
  const sourceDistribution = useMemo(() => {
    const sourceCounts: Record<string, { count: number; value: number }> = {};
    filteredLeads.forEach(lead => {
      const source = lead.source || 'Unknown';
      if (!sourceCounts[source]) {
        sourceCounts[source] = { count: 0, value: 0 };
      }
      sourceCounts[source].count += 1;
      sourceCounts[source].value += lead.value || 0;
    });

    // Sort by count descending
    return Object.entries(sourceCounts)
      .map(([source, data]) => ({ source, ...data }))
      .sort((a, b) => b.count - a.count);
  }, [filteredLeads]);

  // Calculate stats (respects Southwest Airlines exclusion toggle)
  const enrichedLeads = baseLeads.filter((l) => l.apolloEnriched).length;

  // Active campaigns (persisted in localStorage)
  const { activeSequences, totalSequences } = useMemo(() => {
    try {
      const seqs = localStorage.getItem('apollo_analysis_sequences');
      if (seqs) {
        const parsed = JSON.parse(seqs) as { enabled?: boolean }[];
        return {
          activeSequences: parsed.filter(s => s.enabled).length,
          totalSequences: parsed.length,
        };
      }
    } catch { /* ignore */ }
    return { activeSequences: 0, totalSequences: 0 };
  }, []);

  // Stage order matching Monday.com funnel (top to bottom)
  const stageOrder = [
    'Identified',
    'Contacted',
    'Scheduled',
    'Account Qualification',
    'Qualified',
    'Project Identification',
    // Add any other stages that might appear
    'New',
    'Discovery',
    'Proposal',
    'Negotiation',
    'Closed Won',
    'Closed Lost',
  ];

  // Lead stage breakdown - dynamically from actual Monday stages (respects exclusion toggle)
  const stageCountMap = baseLeads.reduce<Record<string, number>>((acc, lead) => {
    const stage = lead.stage || 'Unknown';
    acc[stage] = (acc[stage] || 0) + 1;
    return acc;
  }, {});

  // Sort stages by funnel order, unknown stages go at the end
  const leadsByStage = Object.fromEntries(
    Object.entries(stageCountMap).sort(([a], [b]) => {
      const indexA = stageOrder.findIndex(s => s.toLowerCase() === a.toLowerCase());
      const indexB = stageOrder.findIndex(s => s.toLowerCase() === b.toLowerCase());
      // If not found in order, put at end
      const orderA = indexA === -1 ? 999 : indexA;
      const orderB = indexB === -1 ? 999 : indexB;
      return orderA - orderB;
    })
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Dashboard</h1>
          <p className="mt-1 text-zinc-400">Welcome back. Here's what's happening with your marketing intelligence.</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Active Leads Only Toggle */}
          <label className="flex items-center gap-3 px-4 py-2 rounded-lg bg-zinc-800/50 border border-zinc-700 cursor-pointer hover:bg-zinc-800 transition-colors" title="Hide Closed Won, Closed Lost, and Unqualified leads">
            <span className="text-sm text-zinc-400">Active leads only</span>
            <div className="relative">
              <input
                type="checkbox"
                checked={activeLeadsOnly}
                onChange={(e) => setActiveLeadsOnlyPersist(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-zinc-700 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-emerald-500/50 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-zinc-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-500"></div>
            </div>
          </label>
          {/* Southwest Airlines Toggle */}
          <label className="flex items-center gap-3 px-4 py-2 rounded-lg bg-zinc-800/50 border border-zinc-700 cursor-pointer hover:bg-zinc-800 transition-colors">
            <span className="text-sm text-zinc-400">Include Southwest Airlines</span>
            <div className="relative">
              <input
                type="checkbox"
                checked={includeSouthwestAirlines}
                onChange={(e) => setIncludeSouthwestAirlines(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-zinc-700 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-500/50 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-zinc-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500"></div>
            </div>
          </label>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-5 gap-4">
        <StatCard
          title="Weighted Pipeline"
          value={formatCurrency(weightedPipeline)}
          icon={Gauge}
          subtitle="Probability-adjusted value"
          color="emerald"
          isLoading={dealsLoading}
          tooltip={KPI_TOOLTIPS.weightedPipeline}
        />
        <StatCard
          title="Active Leads"
          value={baseLeads.length.toString()}
          icon={Users}
          subtitle={`${enrichedLeads} enriched`}
          color="blue"
          isLoading={leadsLoading}
        />
        <StatCard
          title="Lead Velocity Rate"
          value={`${leadVelocity.lvr > 0 ? '+' : ''}${leadVelocity.lvr}%`}
          icon={Zap}
          trend={leadVelocity.lvr !== 0 ? { value: Math.abs(leadVelocity.lvr), isPositive: leadVelocity.lvr > 0 } : undefined}
          subtitle={`${leadVelocity.thisMonth} this month vs ${leadVelocity.lastMonth} last month`}
          color={leadVelocity.lvr >= 0 ? 'emerald' : 'orange'}
          isLoading={leadsLoading}
          tooltip={KPI_TOOLTIPS.leadVelocityRate}
        />
        <StatCard
          title="Win Rate"
          value={`${overallWinLossAnalysis.winRate}%`}
          icon={Trophy}
          subtitle={`${overallWinLossAnalysis.wonCount}W / ${overallWinLossAnalysis.lostCount}L • Rolling 1 month`}
          color={overallWinLossAnalysis.winRate >= 50 ? 'emerald' : 'orange'}
          isLoading={dealsLoading}
          tooltip={KPI_TOOLTIPS.winRate}
        />
        <StatCard
          title="Active Sequences"
          value={activeSequences.toString()}
          icon={Megaphone}
          subtitle={`${totalSequences} total`}
          color="orange"
          onClick={() => onNavigate('campaigns')}
        />
      </div>

      {/* Rep Leaderboard - Always visible, shows ALL reps regardless of filter */}
      <RepLeaderboard
        data={leaderboardRepPerformance}
        isLoading={leadsLoading || dealsLoading}
        startDate={leaderboardStartDate}
        endDate={leaderboardEndDate}
        onStartDateChange={setLeaderboardStartDate}
        onEndDateChange={setLeaderboardEndDate}
        defaultDateRange={defaultDateRange}
        leads={dateFilteredLeadsForLeaderboard}
        leadToDealConversion={leaderboardLeadToDealConversion}
        onMetricClick={setAnalyticsDetail}
      />

      {/* Main Content */}
      <div className="space-y-6">
          {/* Lead Pipeline */}
          <div className="rounded-xl border border-zinc-800 bg-zinc-900/50 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-blue-400" />
                <h2 className="text-lg font-semibold text-white">Lead Pipeline</h2>
              </div>
              <Button variant="ghost" size="sm" onClick={() => onNavigate('leads')}>
                View All
                <ArrowUpRight className="h-4 w-4" />
              </Button>
            </div>
            <div className="space-y-3">
              {Object.entries(leadsByStage).map(([stage, count]) => {
                const total = baseLeads.length || 1;
                const percentage = (count / total) * 100;
                return (
                  <div key={stage} className="flex items-center gap-4">
                    <span className="w-24 text-sm text-zinc-400">{stage}</span>
                    <div className="flex-1 h-2 bg-zinc-800 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${getStageColor(stage)}`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                    <span className="w-8 text-sm text-zinc-300 text-right">{count}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Lead Analytics Section - Right under Lead Pipeline */}
          <div className="rounded-xl border border-zinc-800 bg-zinc-900/50 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <LineChart className="h-5 w-5 text-blue-400" />
                <h2 className="text-lg font-semibold text-white">Lead Analytics</h2>
                {hasCustomDateFilter && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-400">
                    Custom filter: {filteredLeads.length} leads, {filteredDeals.length} deals
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-blue-400" />
                <input
                  type="date"
                  value={analyticsStartDate}
                  onChange={(e) => setAnalyticsStartDate(e.target.value)}
                  className="bg-zinc-700 border border-zinc-600 rounded px-2 py-1 text-xs text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/50"
                  placeholder="Start date"
                />
                <span className="text-zinc-400 text-xs">to</span>
                <input
                  type="date"
                  value={analyticsEndDate}
                  onChange={(e) => setAnalyticsEndDate(e.target.value)}
                  className="bg-zinc-700 border border-zinc-600 rounded px-2 py-1 text-xs text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/50"
                  placeholder="End date"
                />
                {hasCustomDateFilter && (
                  <button
                    onClick={resetDateFilter}
                    className="text-xs text-zinc-400 hover:text-white px-2 py-1 rounded hover:bg-zinc-700"
                  >
                    Reset
                  </button>
                )}
              </div>
            </div>

            {/* Rep Filter */}
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
                Rep:
              </span>
              <div className="flex items-center gap-2 flex-wrap">
                <FilterCard
                  label="All"
                  count={repCounts.all ?? 0}
                  isSelected={selectedRep === 'all'}
                  onClick={() => setSelectedRep('all')}
                  icon={Users}
                  color="cyan"
                  size="sm"
                />
                {Object.entries(repCounts)
                  .filter(([key]) => key !== 'all' && key !== 'Unassigned')
                  .sort(([, a], [, b]) => (b ?? 0) - (a ?? 0))
                  .slice(0, 8) // Limit to top 8 reps to save space
                  .map(([rep, count]) => {
                    const initials = rep.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
                    const firstName = rep.split(' ')[0] || rep;
                    const avatarUrl = userPhotoMap[rep];
                    return (
                      <FilterCard
                        key={rep}
                        label={firstName}
                        count={count ?? 0}
                        isSelected={selectedRep === rep}
                        onClick={() => setSelectedRep(rep)}
                        initials={initials}
                        avatar={avatarUrl}
                        color="cyan"
                        size="sm"
                      />
                    );
                  })}
                {hasActiveFilters && (
                  <button
                    onClick={resetAllFilters}
                    className="flex items-center gap-1 px-2 py-1 rounded-lg border border-red-500/50 bg-red-500/10 text-red-400 text-xs hover:bg-red-500/20 transition-colors"
                  >
                    <X className="h-3 w-3" />
                    Clear
                  </button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <StageVelocityCard
                data={stageVelocity}
                leads={filteredLeads}
                deals={filteredDeals}
                isLoading={analyticsLoading}
                onStageClick={setAnalyticsDetail}
              />
              <ConversionFunnelChart
                historicalData={historicalFunnel}
                leads={filteredLeads}
                isLoading={analyticsLoading || stageTransitionsLoading}
                onStageClick={setAnalyticsDetail}
              />
              <LeadAgingTable
                data={leadAging}
                leads={filteredLeads}
                isLoading={analyticsLoading}
                onCellClick={setAnalyticsDetail}
              />
              <ActivityTrendsChart data={activityTrends} isLoading={analyticsLoading} />
              {/* Lead Sources Card */}
              <LeadSourcesCard
                data={sourceDistribution}
                totalLeads={filteredLeads.length}
                isLoading={analyticsLoading}
                onSourceClick={(source, leads) => setAnalyticsDetail({
                  title: `Leads from ${source}`,
                  subtitle: `${leads.length} leads`,
                  leads,
                })}
                filteredLeads={filteredLeads}
                convertedLeads={dateFilteredLeadToDealConversion.convertedLeads}
              />
              {/* Lead to Deal Conversion - Full width */}
              <div className="col-span-2">
                <LeadToDealTracker
                  data={dateFilteredLeadToDealConversion}
                  deals={filteredDeals}
                  isLoading={analyticsLoading}
                  onMetricClick={setAnalyticsDetail}
                />
              </div>
              {/* Slipped Deals Alert */}
              {slippedDeals.length > 0 && (
                <div className="col-span-2">
                  <SlippedDealsPanel
                    deals={slippedDeals}
                    onDealClick={(deals) => setAnalyticsDetail({
                      title: 'Slipped Deals',
                      subtitle: `${deals.length} deals past their expected close date`,
                      deals,
                    })}
                  />
                </div>
              )}
              {/* AI Analysis - Full width */}
              <div className="col-span-2">
                <AIAnalysis
                  leads={filteredLeads}
                  deals={filteredDeals}
                  weightedPipeline={weightedPipeline}
                  leadVelocity={leadVelocity}
                  winLossData={overallWinLossAnalysis}
                  funnel={historicalFunnel}
                  stageVelocity={stageVelocity}
                  leadAging={leadAging}
                  leadToDealConversion={dateFilteredLeadToDealConversion}
                  slippedDeals={slippedDeals}
                  isLoading={analyticsLoading || stageTransitionsLoading}
                />
              </div>
            </div>
          </div>
        </div>

      {/* Analytics Detail Modal */}
      <AnalyticsDetailModal
        data={analyticsDetail}
        onClose={() => setAnalyticsDetail(null)}
      />
    </div>
  );
}

// getStageColor utility used by the pipeline chart
export function getStageColor(stage: string): string {
  const normalizedStage = stage.toLowerCase().trim();

  const colorMap: Record<string, string> = {
    'identified': 'bg-blue-500',
    'contacted': 'bg-sky-500',
    'scheduled': 'bg-cyan-500',
    'qualified': 'bg-teal-500',
    'project identification': 'bg-[#F78C4E]',
    'account qualification': 'bg-emerald-500',
    'new': 'bg-blue-500',
    'new lead': 'bg-blue-500',
    'lead': 'bg-blue-400',
    'contact made': 'bg-sky-500',
    'qualified lead': 'bg-teal-500',
    'mql': 'bg-cyan-400',
    'sql': 'bg-teal-600',
    'discovery': 'bg-[#F78C4E]',
    'meeting': 'bg-[#F78C4E]',
    'demo': 'bg-[#F78C4E]',
    'demo scheduled': 'bg-[#F78C4E]',
    'meeting scheduled': 'bg-[#E67A3C]',
    'proposal': 'bg-orange-500',
    'proposal sent': 'bg-orange-500',
    'quote': 'bg-orange-400',
    'negotiation': 'bg-yellow-500',
    'negotiating': 'bg-yellow-500',
    'in review': 'bg-yellow-400',
    'closed won': 'bg-emerald-500',
    'won': 'bg-emerald-500',
    'customer': 'bg-emerald-400',
    'converted': 'bg-emerald-500',
    'closed lost': 'bg-red-500',
    'lost': 'bg-red-500',
    'on hold': 'bg-zinc-400',
    'nurture': 'bg-purple-500',
    'follow up': 'bg-indigo-500',
  };

  return colorMap[normalizedStage] || 'bg-zinc-500';
}

// NOTE: StatCard, RepLeaderboard, LeadSourcesCard, and SlippedDealsPanel
// have been extracted to separate files. See:
// - ./StatCard.tsx
// - ./analytics/RepLeaderboard.tsx
// - ./analytics/LeadSourcesCard.tsx
// - ./analytics/SlippedDealsPanel.tsx
